import { DEFAULT_WORKFLOW_AREAS, DEFAULT_WORKFLOW_KEYS, DEFAULT_WORKFLOWS, HIGH_IMPACT_DEFAULTS, OPTIONAL_DEFAULT_WORKFLOW_KEYS, type DefaultWorkflowKey } from "@sagot/constants";
import type { WorkflowOut } from "../types";
import type { DefaultImpact } from "./use-defaults";

/** The workflows a workspace was given (origin `system`) apart from the ones it made. */
export function splitByOrigin(all: readonly WorkflowOut[]): { defaults: WorkflowOut[]; own: WorkflowOut[] } {
  return { defaults: all.filter((row) => row.origin === "system"), own: all.filter((row) => row.origin !== "system") };
}

export const isDefault = (workflow: Pick<WorkflowOut, "origin">): boolean => workflow.origin === "system";

const isKnownKey = (key: string | null | undefined): key is DefaultWorkflowKey => DEFAULT_WORKFLOW_KEYS.some((known) => known === key);

/** The alerts that start switched off (the thing they describe does not exist elsewhere): "Off by default", with a "Switch on" button. */
export function isOptional(workflow: Pick<WorkflowOut, "system_key">): boolean {
  return isKnownKey(workflow.system_key) && OPTIONAL_DEFAULT_WORKFLOW_KEYS.has(workflow.system_key);
}

/** The sub-headings in order: the areas of the catalogue, the last one holding the orders and field-service defaults (they arrive with those modules). */
export const LISTED_AREAS = DEFAULT_WORKFLOW_AREAS;
export type ListedArea = (typeof LISTED_AREAS)[number];

const isArea = (area: string | null | undefined): area is ListedArea => LISTED_AREAS.some((known) => known === area);
const rankOf = (workflow: Pick<WorkflowOut, "system_key">): number => {
  const at = DEFAULT_WORKFLOW_KEYS.findIndex((key) => key === workflow.system_key);
  return at < 0 ? DEFAULT_WORKFLOW_KEYS.length : at;
};

export interface AreaGroup {
  area: ListedArea;
  rows: WorkflowOut[];
}

/**
 * The defaults under their sub-headings, in the order of the catalogue. A default whose key a later release dropped (no area)
 * is listed under the first heading rather than disappearing.
 */
export function groupByArea(defaults: readonly WorkflowOut[]): AreaGroup[] {
  const first = LISTED_AREAS[0];
  const areaOf = (row: WorkflowOut): ListedArea => (isArea(row.area) ? row.area : first);
  return LISTED_AREAS.flatMap((area) => {
    const rows = defaults.filter((row) => areaOf(row) === area).sort((a, b) => rankOf(a) - rankOf(b));
    return rows.length > 0 ? [{ area, rows }] : [];
  });
}

/** Defaults that run unless somebody paused them and that someone did pause: the banner above the section lists them. */
export const pausedDefaults = (defaults: readonly WorkflowOut[]): WorkflowOut[] => defaults.filter((row) => row.state === "paused" && !isOptional(row));

/** `module`: paused because a Service module it needs is switched off (never by a person). */
export type DefaultStatus = "live" | "paused" | "off" | "module";

/** Live, paused, or an optional alert nobody switched on yet. */
export function statusOf(workflow: Pick<WorkflowOut, "state" | "system_key">): DefaultStatus {
  if (workflow.state === "live") return "live";
  return isOptional(workflow) ? "off" : "paused";
}

/** Whether "Restore default" has something to do: the workflow was edited, a saved draft is not published, or the catalogue has a newer revision. */
export const canRestore = (workflow: Pick<WorkflowOut, "is_modified" | "has_default_update" | "has_unpublished_changes">): boolean =>
  workflow.is_modified || workflow.has_default_update || workflow.has_unpublished_changes;

/** What a default delivers: a developer endpoint gets deliveries, everyone else alerts (the words of the pause button). */
export const deliversEvents = (workflow: Pick<WorkflowOut, "area">): boolean => workflow.area === "developers";

export type ImpactKind = "alerts" | "mailboxes" | "deliveries" | "assignments";
export interface ImpactLine {
  kind: ImpactKind;
  count: number;
  /** The second number of the sentence ("alerts to {people} people"). */
  people?: number;
}

/** What pausing stops, one line per thing that happened in the window; empty when nothing did. */
export function impactLines(impact: Pick<DefaultImpact, "notifications" | "recipients" | "mailboxes" | "deliveries" | "assignments">): ImpactLine[] {
  const lines: ImpactLine[] = [];
  if (impact.notifications > 0) lines.push({ kind: "alerts", count: impact.notifications, people: impact.recipients });
  if (impact.mailboxes > 0) lines.push({ kind: "mailboxes", count: impact.mailboxes });
  if (impact.deliveries > 0) lines.push({ kind: "deliveries", count: impact.deliveries });
  if (impact.assignments > 0) lines.push({ kind: "assignments", count: impact.assignments });
  return lines;
}

/** `WD-04` for a Service desk default (the number the scope and the mockups use); `null` for the earlier ones, which have none. */
export function wdOf(workflow: Pick<WorkflowOut, "system_key">): string | null {
  return DEFAULT_WORKFLOWS.find((entry) => entry.systemKey === workflow.system_key)?.wd ?? null;
}

const HIGH_IMPACT: readonly string[] = HIGH_IMPACT_DEFAULTS;

/** A default clients or staff depend on (WD-04, WD-17, WD-28): pausing it asks for its name (WF-009). Read from the key, for when the numbers cannot be read. */
export const isHighImpactKey = (systemKey: string | null | undefined): boolean => systemKey !== null && systemKey !== undefined && HIGH_IMPACT.includes(systemKey);

export type StateFilter = "" | "live" | "paused" | "off" | "modified";
export type KindFilter = "" | "facing" | "background";

/** What the list is narrowed to: a search, the state, the kind, the area. */
export interface ListFilters {
  q: string;
  state: StateFilter;
  kind: KindFilter;
  area: "" | ListedArea;
}

export const NO_FILTERS: ListFilters = { q: "", state: "", kind: "", area: "" };
export const isFiltering = (filters: ListFilters): boolean => filters.q.trim() !== "" || filters.state !== "" || filters.kind !== "" || filters.area !== "";

const areaOfRow = (row: Pick<WorkflowOut, "area">): ListedArea => (isArea(row.area) ? row.area : LISTED_AREAS[0]);

/** The defaults that answer the search (name, trigger, key, WD number, description) and the filters. */
export function filterDefaults(defaults: readonly WorkflowOut[], filters: ListFilters): WorkflowOut[] {
  const q = filters.q.trim().toLowerCase();
  return defaults.filter((row) => {
    if (q && ![row.name, row.trigger, row.system_key ?? "", wdOf(row) ?? "", row.description ?? ""].join(" ").toLowerCase().includes(q)) return false;
    if (filters.state === "modified" ? !row.is_modified : filters.state !== "" && statusOf(row) !== filters.state) return false;
    if (filters.kind !== "" && row.kind !== filters.kind) return false;
    return filters.area === "" || areaOfRow(row) === filters.area;
  });
}
